import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ControlBar,
  GridLayout,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  useConnectionState,
  useRemoteParticipants,
  useTracks,
  useRoomContext,
  useMediaDeviceSelect,
} from "@livekit/components-react";
import { ConnectionState, Track, type MediaDeviceFailure } from "livekit-client";
import toast from "@/components/ui/toast";
import Notification from "@/components/ui/Notification";
import {
  AutoEnableTracks,
  mediaFailureMessage,
  useVideoRoomConnection,
} from "@/custolink-views/shared/useVideoRoomConnection";
import type { VideoRoomRole, VideoRoomEndedInfo } from "@/custolink-views/shared/useVideoRoomConnection";
import { GUEST_ROOM_OPTIONS } from "@/custolink-views/shared/guestPublishDefaults";
import { RecordingConsentBanner } from "@/custolink-views/shared/recording_consent/RecordingConsentBanner";
import {
  guestRecordingConsentApi,
  workerRecordingConsentApi,
  type RecordingConsentApi,
} from "@/custolink-views/shared/recording_consent/recordingConsentApi";
import { tokenCanPublish } from "@/custolink-views/shared/recording_consent/recordingStatus";
import { useRecordingConsent } from "@/custolink-views/shared/recording_consent/useRecordingConsent";

export type { VideoRoomRole, VideoRoomEndedInfo } from "@/custolink-views/shared/useVideoRoomConnection";

function CameraSwitchButton() {
  const room = useRoomContext();
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: "videoinput",
    room,
  });

  if (devices.length <= 1) return null;

  const handleSwitch = () => {
    const currentIndex = devices.findIndex((d) => d.deviceId === activeDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    setActiveMediaDevice(devices[nextIndex].deviceId);
  };

  return (
    <button
      title="Switch camera"
      style={{
        position: "absolute",
        top: "12px",
        right: "12px",
        zIndex: 10,
        background: "rgba(0,0,0,0.45)",
        border: "none",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
      onClick={handleSwitch}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 7h-3a2 2 0 0 1-2-2V2" />
        <path d="M9 2H4a2 2 0 0 0-2 2v4" />
        <path d="M4 17H2v4h4" />
        <path d="M22 14v4a2 2 0 0 1-2 2h-4" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    </button>
  );
}

function Spinner() {
  return <div className="video-room-spinner" aria-hidden="true" />;
}

/**
 * Counts seconds since `active` became true, so the worker can see how long the
 * invite has been outstanding.
 */
function useElapsedSeconds(active: boolean) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [active]);

  return seconds;
}

function formatElapsed(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Renders what the participant needs to understand the current state of the
 * room: connecting, waiting for the other side, and reconnecting.
 * Must be mounted inside LiveKitRoom.
 */
function RoomStatus(p: { role: VideoRoomRole; inviteLabel?: string }) {
  const room = useRoomContext();
  const connectionState = useConnectionState(room);
  const remoteParticipants = useRemoteParticipants();

  const connecting =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Disconnected;
  const reconnecting =
    connectionState === ConnectionState.Reconnecting ||
    connectionState === ConnectionState.SignalReconnecting;
  const waiting =
    connectionState === ConnectionState.Connected &&
    remoteParticipants.length === 0;

  const elapsed = useElapsedSeconds(waiting);

  if (reconnecting) {
    return (
      <div className="video-room-banner" role="status">
        <Spinner />
        <span>Connection lost - reconnecting…</span>
      </div>
    );
  }

  if (connecting) {
    return (
      <div className="video-room-overlay" role="status">
        <Spinner />
        <p className="video-room-overlay-title">Connecting…</p>
      </div>
    );
  }

  if (waiting) {
    return (
      <div className="video-room-overlay" role="status">
        <Spinner />
        {p.role === "worker" ? (
          <>
            <p className="video-room-overlay-title">
              {p.inviteLabel ? `Invite sent to ${p.inviteLabel}` : "Invite sent"}
            </p>
            <p className="video-room-overlay-text">
              Waiting for them to open the link and join. You can stay on this
              screen.
            </p>
          </>
        ) : (
          <>
            <p className="video-room-overlay-title">Connecting you to support</p>
            <p className="video-room-overlay-text">
              Please stay on this screen. Someone will join shortly.
            </p>
          </>
        )}
        <p className="video-room-overlay-timer">{formatElapsed(elapsed)}</p>
      </div>
    );
  }

  return null;
}

/**
 * Tells everyone in the room that the call is recorded.
 *
 * Shown from the moment the room opens rather than when egress actually starts: recording
 * begins as soon as the second participant joins, so anything that waited for confirmation
 * would be telling people after the fact.
 *
 * Fallback only, for a room with no consent API: when one is available the D-09 state from the
 * server replaces this static pill (see RecordingConsentBanner), so the room never claims more
 * than the server confirms. Without a consent API this stays exactly as it was (R-03).
 *
 * Whether it appears at all comes from the server, alongside the token - the worker can
 * untick "Record this call" when sending the invite, and recording can be off for the
 * deployment entirely. Deciding that in the browser would risk the notice disagreeing with
 * what actually happens, in either direction.
 */
function RecordingNotice() {
  return (
    <div className="video-room-recording" role="status">
      <span className="video-room-recording-dot" />
      <span>This call is being recorded</span>
    </div>
  );
}

function VideoConferenceNoChat(p: {
  role: VideoRoomRole;
  inviteLabel?: string;
  recording: boolean;
  token: string;
  consentApi: RecordingConsentApi | null;
  onMediaFailure: (failure: MediaDeviceFailure) => void;
  onEndSession?: () => void;
  endSessionPending?: boolean;
}) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  // D-05/D-09 live here because the hook needs the room context: it reads cl.state, answers
  // the consent question and, for a held participant, brings the media up on the D-06 grant.
  const consent = useRecordingConsent({
    api: p.consentApi,
    token: p.token,
    role: p.role,
    onMediaFailure: p.onMediaFailure,
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ControlBar controls={{ chat: false }} />
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <GridLayout tracks={tracks} style={{ height: "100%" }}>
          <ParticipantTile />
        </GridLayout>
        <CameraSwitchButton />
        {consent ? (
          <RecordingConsentBanner consent={consent} role={p.role} />
        ) : (
          p.recording && <RecordingNotice />
        )}
        <RoomStatus role={p.role} inviteLabel={p.inviteLabel} />
        {p.role === "worker" && p.onEndSession != null && (
          <button
            className="video-room-end-session"
            title="End this session for everyone"
            disabled={p.endSessionPending}
            onClick={p.onEndSession}
          >
            End session
          </button>
        )}
      </div>
    </div>
  );
}

function BlockingMessage(p: {
  title: string;
  children: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  /** A non-destructive way out, e.g. "Keep the call" next to "End anyway". */
  secondaryLabel?: string;
  onSecondary?: () => void;
}) {
  return (
    <div className="video-room-blocking">
      <div className="video-room-blocking-card">
        <h4 className="video-room-blocking-title">{p.title}</h4>
        <div className="video-room-blocking-text">{p.children}</div>
        {(p.actionLabel != null || p.secondaryLabel != null) && (
          <div className="video-room-blocking-actions">
            {p.secondaryLabel != null && p.onSecondary != null && (
              <button
                className="video-room-blocking-button video-room-blocking-button-secondary"
                disabled={p.actionDisabled}
                onClick={p.onSecondary}
              >
                {p.secondaryLabel}
              </button>
            )}
            {p.actionLabel != null && p.onAction != null && (
              <button
                className="video-room-blocking-button"
                disabled={p.actionDisabled}
                onClick={p.onAction}
              >
                {p.actionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The video room itself. Router-independent, so it can be rendered both as a
 * standalone page (the worker popup) and inline inside another page (the
 * recipient's invite page, where a popup would be blocked on mobile).
 */
export function VideoRoom(p: {
  token: string;
  serverUrl: string;
  role: VideoRoomRole;
  /** Present only when this browser is allowed to close the call server-side. */
  orgId?: string | null;
  callId?: string | null;
  /** The public room id, which is how a guest reaches the room-scoped consent API. */
  roomId?: string | null;
  /** Shown in the waiting state on the worker side, e.g. a phone number. */
  inviteLabel?: string;
  /** Whether to show the recording notice. Comes from the server with the token. */
  recording?: boolean;
  onEnded: (info: VideoRoomEndedInfo) => void;
}) {
  const { token, serverUrl, orgId, callId, roomId, onEnded } = p;

  const conn = useVideoRoomConnection({
    orgId,
    callId,
    onEnded,
    onEndSessionError: (m) => toast.push(<Notification title={m} type="danger" />),
  });

  // Worker popup carries orgId and callId, so it gets the org-scoped admin API; a guest carries
  // only roomId, so it gets the room-scoped public API. A browser with neither has no consent
  // surface at all (T-06.1-44: a guest can never reach the worker API).
  const consentApi = useMemo<RecordingConsentApi | null>(() => {
    if (orgId && callId) {
      return workerRecordingConsentApi(Number(orgId), Number(callId));
    }
    if (roomId) {
      return guestRecordingConsentApi(roomId);
    }
    return null;
  }, [orgId, callId, roomId]);

  // D-06: a token that withholds publishing keeps the camera and microphone down until the
  // server grants it. useRecordingConsent brings them up on the grant.
  const held = useMemo(() => !tokenCanPublish(token), [token]);

  if (!token || !serverUrl) {
    return (
      <div className="video-room-root">
        <BlockingMessage title="Missing video credentials">
          This link is incomplete. Please open the invite link again.
        </BlockingMessage>
      </div>
    );
  }

  const media = conn.mediaFailure != null ? mediaFailureMessage(conn.mediaFailure) : null;

  return (
    <div className="video-room-root">
      <LiveKitRoom
        key={conn.attempt}
        token={token}
        serverUrl={serverUrl}
        connect={true}
        video={!held}
        audio={!held}
        options={p.role === "customer" ? GUEST_ROOM_OPTIONS : undefined}
        style={{ height: "100%" }}
        onConnected={conn.handleConnected}
        onDisconnected={conn.handleDisconnected}
        onError={conn.handleError}
        onMediaDeviceFailure={conn.handleMediaDeviceFailure}
      >
        <AutoEnableTracks enabled={!held} onFailure={conn.reportMediaFailure} />
        <VideoConferenceNoChat
          role={p.role}
          inviteLabel={p.inviteLabel}
          recording={p.recording === true}
          token={token}
          consentApi={consentApi}
          endSessionPending={conn.endSessionPending}
          onMediaFailure={conn.reportMediaFailure}
          onEndSession={conn.canEndSession ? conn.requestEndSession : undefined}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>

      {conn.endConfirm && (
        <BlockingMessage
          title="A customer is still in this call"
          actionLabel="End anyway"
          secondaryLabel="Keep the call"
          actionDisabled={conn.endSessionPending}
          onAction={conn.confirmEndSession}
          onSecondary={conn.cancelEndConfirm}
        >
          Ending the session will disconnect them.
        </BlockingMessage>
      )}

      {media != null && (
        <BlockingMessage
          title={media.title}
          actionLabel="Try again"
          onAction={conn.retry}
        >
          {media.body}
        </BlockingMessage>
      )}

      {media == null && conn.roomError != null && (
        <BlockingMessage
          title="Could not connect"
          actionLabel="Try again"
          onAction={conn.retry}
        >
          {conn.roomError.message ||
            "Something went wrong while connecting to the video call."}
        </BlockingMessage>
      )}

      {media == null && conn.roomError == null && conn.failedBeforeConnecting && (
        <BlockingMessage
          title="Could not connect"
          actionLabel="Try again"
          onAction={conn.retry}
        >
          The video service closed the connection before the call started.
          Check that the video server is reachable from this device.
        </BlockingMessage>
      )}
    </div>
  );
}

/**
 * Standalone page used by the worker popup (/admin/video-room) and kept for the
 * public /video-room route. The recipient's invite page renders VideoRoom
 * inline instead, so no popup is needed there.
 */
export default function VideoRoomPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const serverUrl = params.get("serverUrl") ?? "";
  const orgId = params.get("orgId");
  const callId = params.get("callId");
  const inviteLabel = params.get("inviteLabel") ?? undefined;
  // Set by the opener from the server's answer, not decided here. Absent means no
  // notice, so a link built without it never claims a call is recorded.
  const recording = params.get("recording") === "1";
  // The window is opened during the click, before the call exists, so the
  // popup blocker cannot swallow it. See reserveWorkerVideoRoom.
  const preparing = params.get("preparing") != null;

  const handleEnded = useCallback(() => {
    // Tell the opener however the call ended, not only when the remote side hung up. The
    // Support Hub is a different document and cannot see the call close otherwise, so a
    // worker who ended their own call had to reload the page before it showed up in the list.
    window.opener?.postMessage("call-ended", window.location.origin);
    window.close();
  }, []);

  if (preparing && !token) {
    return (
      <div className="video-room-root">
        <div className="video-room-overlay" role="status">
          <Spinner />
          <p className="video-room-overlay-title">Preparing your video call…</p>
          <p className="video-room-overlay-text">
            Sending the invite. This window will connect on its own.
          </p>
        </div>
      </div>
    );
  }

  return (
    <VideoRoom
      token={token}
      serverUrl={serverUrl}
      role="worker"
      orgId={orgId}
      callId={callId}
      inviteLabel={inviteLabel}
      recording={recording}
      onEnded={handleEnded}
    />
  );
}
